import React, { useState, useEffect } from "react";
import "./Contact.css";

const Contact = () => {
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState("");

  // Fetch contact page content from WordPress
  useEffect(() => {
    const fetchContactPage = async () => {
      try {
        // ✅ Removed trailing spaces
        const response = await fetch(
          "https://cms.vxpath.com/wp-json/wp/v2/pages?slug=contact"
        );
        if (!response.ok) throw new Error("Failed to fetch Contact page");
        const pages = await response.json();
        if (pages.length === 0) throw new Error("Contact page not found");

        const acf = pages[0].acf || {};
        setContactInfo({
          hq: {
            title: acf.hq_title || "Global Headquarters",
            address: acf.hq_address || "123 Innovation Drive\nTech City, TC 10101",
          },
          support: {
            title: acf.support_title || "Support",
            phone: acf.support_phone || "+1 (800) VX-PATH",
            email: acf.support_email || "support@vxpath.tech",
          },
          sales: {
            title: acf.sales_title || "Sales Inquiries",
            email: acf.sales_email || "sales@vxpath.tech",
            phone: acf.sales_phone || "+1 (888) VX-SOLUTIONS",
          },
        });
        setLoading(false);
      } catch (err) {
        console.error("Error fetching contact page:", err);
        setLoading(false);
      }
    };

    fetchContactPage();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Frontend-only validation
  const validateForm = () => {
    const { name, email, company, message } = formData;

    if (!name.trim()) {
      setErrorMessage("Please enter your full name.");
      return false;
    }

    if (!email.trim()) {
      setErrorMessage("Please enter your business email.");
      return false;
    }

    // Simple email regex (sufficient for frontend)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage("Please enter a valid email address.");
      return false;
    }

    if (!company.trim()) {
      setErrorMessage("Please enter your company name.");
      return false;
    }

    if (!message.trim()) {
      setErrorMessage("Please enter your message.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSubmitStatus(null);

    // ✅ FRONTEND VALIDATION ONLY
    if (!validateForm()) {
      setSubmitStatus("error");
      return;
    }

    setSubmitting(true);

    try {
      // ✅ Removed trailing spaces
      const response = await fetch(
        "https://cms.vxpath.com/wp-json/vxpath/v1/contact",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      // Even if backend fails, we treat it as network issue
      if (response.ok) {
        setSubmitStatus("success");
        setFormData({ name: "", email: "", company: "", message: "" });
      } else {
        // Generic fallback (since we don't rely on backend validation)
        setSubmitStatus("error");
        setErrorMessage("Failed to send. Please try again or email us directly.");
      }
    } catch (err) {
      console.error("Network error:", err);
      setSubmitStatus("error");
      setErrorMessage("Unable to connect. Please check your internet or try later.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="contact-page">
        <div className="contact-section">
          <p>Loading contact information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-page">
      <header className="page-header">
        <h1>Contact Us</h1>
        <p>Let’s build your digital future together</p>
      </header>

      <section className="contact-section">
        <div className="contact-container">
          {/* Contact Info Cards */}
          <div className="contact-info">
            <div className="info-card" aria-labelledby="hq-heading">
              <h3 id="hq-heading">{contactInfo.hq.title}</h3>
              <p>
                {contactInfo.hq.address.split("\n").map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </p>
            </div>

            <div className="info-card" aria-labelledby="support-heading">
              <h3 id="support-heading">{contactInfo.support.title}</h3>
              <p>
                24/7 Emergency Line:{" "}
                <a
                  href={`tel:${contactInfo.support.phone.replace(/\D/g, "")}`}
                  aria-label={`Call ${contactInfo.support.phone}`}
                >
                  {contactInfo.support.phone}
                </a>
                <br />
                <a
                  href={`mailto:${contactInfo.support.email}`}
                  aria-label={`Email ${contactInfo.support.email}`}
                >
                  {contactInfo.support.email}
                </a>
              </p>
            </div>

            <div className="info-card" aria-labelledby="sales-heading">
              <h3 id="sales-heading">{contactInfo.sales.title}</h3>
              <p>
                <a
                  href={`mailto:${contactInfo.sales.email}`}
                  aria-label={`Email ${contactInfo.sales.email}`}
                >
                  {contactInfo.sales.email}
                </a>
                <br />
                <a
                  href={`tel:${contactInfo.sales.phone.replace(/\D/g, "")}`}
                  aria-label={`Call ${contactInfo.sales.phone}`}
                >
                  {contactInfo.sales.phone}
                </a>
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <h2>Send us a message</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">
                  Full Name <span className="required-asterisk">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  aria-describedby="name-required"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  Business Email <span className="required-asterisk">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  aria-describedby="email-required"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="company">
                Company Name <span className="required-asterisk">*</span>
              </label>
              <input
                id="company"
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                aria-describedby="company-required"
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">
                How can we help you? <span className="required-asterisk">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                aria-describedby="message-required"
                rows="5"
              ></textarea>
            </div>

            {submitStatus === "success" && (
              <p className="form-feedback success">
                Thank you! We’ll get back to you soon.
              </p>
            )}
            {submitStatus === "error" && errorMessage && (
              <p className="form-feedback error">{errorMessage}</p>
            )}

            <button
              type="submit"
              className="btn-primary"
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;